import { isNil } from 'lodash'

import { Nullable } from '@shared/models/types/primary'

export const sessionStorageGet = <T extends string = string>(
  key: string,
  // eslint-disable-next-line
  fallback: any = null
): T => {
  const item = sessionStorage.getItem(key)

  if (!item) {
    return fallback
  }

  return item as T
}
// eslint-disable-next-line
function handleBigIntRecursively(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(handleBigIntRecursively)
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        handleBigIntRecursively(value),
      ])
    )
  } else if (typeof obj === 'bigint') {
    return obj.toString()
  }
  return obj
}

export const sessionStorageSave = (
  key: string,
  // eslint-disable-next-line
  payload?: Nullable<Record<string, any> | string | any | number | []>
) => {
  if (isNil(payload)) {
    // eslint-disable-next-line
    console.log('sessionStorage helper: ommiting to set an empty value')
    return
  }

  const valueType = typeof payload

  const stringifyTypes = ['object', 'array']

  if (stringifyTypes.includes(valueType)) {
    const payloadWithBigIntHandled = JSON.stringify(
      handleBigIntRecursively(payload)
    )

    sessionStorage.setItem(key, payloadWithBigIntHandled)
    return
  }

  sessionStorage.setItem(key, `${payload}`)
}

export const sessionStorageRemove = (key: string) =>
  sessionStorage.removeItem(key)

export const sessionStorageHas = (key: string) =>
  sessionStorageGet(key) !== null

export const wrapSessionStorageGet = <T = unknown>(
  key: string,
  // eslint-disable-next-line
  fallback: any = null
): T => {
  const rawData = sessionStorageGet<string>(key, fallback)

  if (typeof rawData === 'string') {
    try {
      return JSON.parse(rawData) as T
    } catch (e) {
      /* eslint-disable no-console */
      console.warn(
        `Failed to parse JSON for key "${key}". Returning raw value.`,
        e
      )
    }
  }

  // Return the raw data if not a string
  return rawData as T
}
export const sessionStorageGetObject = <T = unknown>(
  key: string,
  // eslint-disable-next-line
  fallback: any = null
): T => {
  const rawData = sessionStorageGet<string>(key, fallback)

  if (typeof rawData === 'string') {
    try {
      // Attempt to parse the string as JSON
      return JSON.parse(rawData) as T
    } catch (e) {
      // Return rawData if parsing fails
      console.warn(
        `Failed to parse JSON for key "${key}". Returning raw value.`,
        e
      )
    }
  }

  // Return the raw data if not a string
  return rawData as T
}

export enum SessionStorageKeys {
  SelfShedulerUserInfo = 'selfShedulerUserInfo',
  FaqSearch = 'faqSearch',
  Locale = 'locale',
  SelectedRoute = 'selectedRoute',
  SelectedStrategy = 'selectedStrategy',
  TimerValue = 'timerValue',
  AreTwoRoutesAvailable = 'areTwoRoutesAvailable',
  OncallSortingMode = 'oncallSortingMode',
  QuickFormSortingMode = 'quickFormSortingMode',
  TemplateSortingMode = 'templateSortingMode',
  QliqConversationsList = 'qliqConversationsList', // Key for the list of Sip conversations
  QliqMessagesPrefix = 'qliqMessages_', // Prefix for Sip messages scoped by conversationId
}
